/**
 * 🛏️ **THE RÚM — the bed (D133).** Where it stands, as cells.
 *
 * 🎯 **haugur, 2026-10-02:** *"lets build a bed, when i lay in my bed and sleep
 * my caracter and all animals are imune to time … bed is crafted, it should take
 * planks and wool. i can place my bed anywhere"* — and his picks: the teppi D
 * (`Design/samples/161`) and the bed D, a plank bed on legs (`162`).
 *
 * 📐 **Two cells, like Minecraft's.** The **head** (`rum`) is the cell you
 * clicked and carries the facing; the **foot** (`rumhluti`) is the next cell
 * AWAY from you, so the bed stretches out in front of you as a house does. The
 * foot has no state of its own: it is found by asking which neighbour is a head
 * pointing at it ({@link rumHeadFor}).
 *
 * 🔴 **It is NOT a house.** `HOUSES` (bur.ts) means a 5 × 9 × 5 building with a
 * carved board and a menu, and listing a bed there would give it all of that.
 * It borrows only the direction helpers, so "which way is away from the player"
 * has one answer in the whole game.
 *
 * Pure: no server, no three.js.
 */

import { blockIdForKey } from './blocks.ts';
import { BUR_FACINGS, burFrame, type BurFacing } from './bur.ts';
import { baseBlockOf, stateOf, withState } from './states.ts';

export const RUM_BLOCK = 'rum';
export const RUM_PART = 'rumhluti';
/** The item. Placing it stamps the head and the foot. */
export const RUM_ITEM = 'rum';

interface Cell {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** 🛏️ Is this id a bed's head, in any facing? */
export function isRum(id: number): boolean {
  const base = blockIdForKey(RUM_BLOCK);
  return base !== undefined && baseBlockOf(id) === base;
}

/** 🛏️ Is this id a bed's foot? */
export function isRumPart(id: number): boolean {
  const part = blockIdForKey(RUM_PART);
  return part !== undefined && id === part;
}

export function rumPartId(): number | undefined {
  return blockIdForKey(RUM_PART);
}

/** Which way a head faces, or `undefined` for anything that is not a head. */
export function rumFacing(id: number): BurFacing | undefined {
  if (!isRum(id)) return undefined;
  const facing = stateOf(id)['facing'];
  return typeof facing === 'string' && (BUR_FACINGS as readonly string[]).includes(facing)
    ? (facing as BurFacing)
    : 'north';
}

export function rumIdFor(facing: BurFacing): number | undefined {
  const base = blockIdForKey(RUM_BLOCK);
  return base === undefined ? undefined : withState(base, { facing });
}

/** 🛏️ **The foot**: the cell one step away from the player who placed the head. */
export function rumFoot(head: Cell, facing: BurFacing): Cell {
  const { into } = burFrame(facing);
  return { x: head.x + into.x, y: head.y, z: head.z + into.z };
}

/**
 * 🛏️ **Whose foot is this?** — the head beside it that points at it, or
 * `undefined`.
 *
 * ⚠️ **Asked of the four neighbours, never assumed.** Two beds can stand foot
 * to foot, and a foot is only ever the foot of the head that names it.
 */
export function rumHeadFor(
  foot: Cell,
  blockAt: (x: number, y: number, z: number) => number,
): Cell | undefined {
  for (const [dx, dz] of [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const) {
    const head = { x: foot.x + dx, y: foot.y, z: foot.z + dz };
    const facing = rumFacing(blockAt(head.x, head.y, head.z));
    if (facing === undefined) continue;
    const f = rumFoot(head, facing);
    if (f.x === foot.x && f.z === foot.z) return head;
  }
  return undefined;
}

/**
 * 🟫 **Can a bed go here?** Both cells empty, and solid ground under both.
 *
 * ⚠️ One function for the client's shadow and the server's refusal, so the two
 * can never disagree — the rule `plotVerdict` keeps for the houses.
 */
export function rumPlotVerdict(
  head: Cell,
  facing: BurFacing,
  blockAt: (x: number, y: number, z: number) => number,
  isSolid: (id: number) => boolean,
): 'free' | 'blocked' | 'unlevel' {
  const foot = rumFoot(head, facing);
  for (const c of [head, foot]) if (blockAt(c.x, c.y, c.z) !== 0) return 'blocked';
  for (const c of [head, foot]) {
    const g = blockAt(c.x, c.y - 1, c.z);
    if (g === 0 || !isSolid(g)) return 'unlevel';
  }
  return 'free';
}

/**
 * 📍 **The middle of the bed**, on its floor — where the model's centre goes,
 * in cell units: half a step from the head toward the foot. Add 0.5 to x and z
 * for the cell's own centre, as `burCentre` asks.
 */
export function rumCentre(head: Cell, facing: BurFacing): { x: number; y: number; z: number } {
  const { into } = burFrame(facing);
  return { x: head.x + into.x * 0.5, y: head.y, z: head.z + into.z * 0.5 };
}

/**
 * 🧍 **Where you stand when you wake** — beside the bed, on the side to the
 * placer's right, half way along. ⚠️ Not on it: the bed is solid half a metre,
 * and standing up inside a bed would be a body stuck in a block.
 */
export function rumWakeSpot(head: Cell, facing: BurFacing): { x: number; y: number; z: number } {
  const { into, right } = burFrame(facing);
  return {
    x: head.x + 0.5 + into.x * 0.5 + right.x,
    y: head.y,
    z: head.z + 0.5 + into.z * 0.5 + right.z,
  };
}
